import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import Alert from '../../../components/Alert';
import constants from '../../../constants';

const InvoiceModal = ({ isOpen, requestId, onClose }) => {
	const modalRef = useRef();
	const invoiceQuery = useQuery({
		queryKey: ['invoice', requestId],
		queryFn: async () => {
			const { data } = await axios.get(`/requests/${requestId}/get-line-items`);
			return data;
		},
		enabled: false,
	});
	const lineItemMutation = useMutation({
		mutationFn: async (lineItem) => {
			const { data } = await axios.post(
				`/requests/${requestId}/fulfill-line-item/${lineItem.id}`,
				{ status: lineItem.status },
			);
			return data;
		},
		onSuccess: () => {
			invoiceQuery.refetch();
		},
		onError: (error) => {
			console.error('Error fulfilling line item:', error);
		},
	});
	useEffect(() => {
		if (isOpen) {
			modalRef.current.showModal();
			invoiceQuery.refetch();
		}
	}, [isOpen]);

	return (
		<dialog id="invoice-dialog" className="modal modal-top" ref={modalRef}>
			<div className="modal-box">
				<h3>Service Request Line Items</h3>
				<div className="py-4">
					{invoiceQuery.isLoading && <p>Loading...</p>}
					{invoiceQuery.isError && (
						<Alert message="There was a problem loading line items!" />
					)}
					{invoiceQuery.isSuccess && (
						<table className="table w-full">
							<thead>
								<tr>
									<th></th>
									<th>SKU</th>
									<th>Scheduled Date</th>
									<th>Service Unit Cost</th>
									<th>Fulfilled Date</th>
									<th>Fulfillment Status</th>
									<th>Fulfill?</th>
								</tr>
							</thead>
							<tbody>
								{invoiceQuery.data.ilos_claims.map((item, index) => (
									<tr key={item.id}>
										<td>
											{index + 1} of {invoiceQuery.data.ilos_claims.length}
										</td>
										<td>
											{item.taxonomy_sku.sku_number} {item.taxonomy_sku.label}
										</td>
										<td>{format(new Date(item.incurred_dttm), 'P')}</td>
										<td>${item.ilos_authorized_service.service_unit_cost}</td>
										<td>
											{!isEmpty(item.fulfilled_dttm) &&
												format(new Date(item.fulfilled_dttm), 'P')}
										</td>
										<td>
											{!isEmpty(item.fulfilled_status) &&
												constants.fulfillmentStatus[item.fulfilled_status]}
										</td>
										<td>
											{isEmpty(item.fulfilled_dttm) && (
												<details className="dropdown dropdown-end">
													<summary className="btn btn-sm btn-primary">
														Complete?
													</summary>
													<ul className="menu dropdown-content bg-base-100 rounded-box z-[1] w-52 p-2 shadow">
														<li>
															<a
																onClick={() =>
																	lineItemMutation.mutate({
																		...item,
																		status: 'FULFILLED',
																	})
																}
															>
																Fulfill
															</a>
														</li>
														<li
															onClick={() =>
																lineItemMutation.mutate({
																	...item,
																	status: 'NOT_REIMBURSEABLE',
																})
															}
														>
															<a>Not Reimbursable</a>
														</li>
														<li
															onClick={() =>
																lineItemMutation.mutate({
																	...item,
																	status: 'NOT_ELIGIBLE',
																})
															}
														>
															<a>Not Eligible</a>
														</li>
													</ul>
												</details>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
				<div className="modal-action">
					<form method="dialog">
						<button className="btn btn-primary" onClick={onClose}>
							Close
						</button>
					</form>
				</div>
			</div>
		</dialog>
	);
};

InvoiceModal.propTypes = {
	isOpen: PropTypes.bool,
	requestId: PropTypes.string,
	onClose: PropTypes.func.isRequired,
};

export default InvoiceModal;
